import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {fetchMovieCrosswordManifest} from '../utilities/dataSources';
import {mobile} from '../services/mobileBridge';

type Direction = 'across' | 'down';

type Cell = {
    row: number;
    column: number;
};

type CrosswordEntry = {
    number: number;
    direction: Direction;
    row: number;
    column: number;
    length: number;
    clue: string;
    answer?: string;
    displayAnswer?: string;
    trivia?: string;
    movie?: string;
    year?: number;
};

type CrosswordManifest = {
    schemaVersion: number;
    date: string;
    generatedAt: string;
    title: string;
    theme: string;
    difficulty: string;
    movieYearRange?: { min: number; max: number };
    grid: {
        rows: number;
        columns: number;
        pattern: string[];
        solution: string[];
        entries: CrosswordEntry[];
    };
};

function cellKey(cell: Cell) {
    return `${cell.row}:${cell.column}`;
}

function entryKey(entry: CrosswordEntry) {
    return `${entry.number}-${entry.direction}`;
}

function containsCell(entry: CrosswordEntry, cell: Cell) {
    if (entry.direction === 'across') {
        return cell.row === entry.row
            && cell.column >= entry.column
            && cell.column < entry.column + entry.length;
    }
    return cell.column === entry.column
        && cell.row >= entry.row
        && cell.row < entry.row + entry.length;
}

function entryCells(entry: CrosswordEntry) {
    return Array.from({length: entry.length}, (_, index) => ({
        row: entry.row + (entry.direction === 'down' ? index : 0),
        column: entry.column + (entry.direction === 'across' ? index : 0),
    }));
}

function nextOpenCell(manifest: CrosswordManifest, from: Cell, direction: Direction, step: number) {
    const delta = direction === 'across' ? {row: 0, column: step} : {row: step, column: 0};
    let row = from.row + delta.row;
    let column = from.column + delta.column;
    const maxSteps = Math.max(manifest.grid.rows, manifest.grid.columns);

    for (let index = 0; index < maxSteps; index += 1) {
        if (
            row >= 0
            && row < manifest.grid.rows
            && column >= 0
            && column < manifest.grid.columns
            && manifest.grid.pattern[row]?.[column] !== '#'
        ) {
            return {row, column};
        }
        row += delta.row;
        column += delta.column;
    }
    return null;
}

function formatPuzzleDate(date?: string) {
    if (!date) return '';
    const parsed = new Date(`${date}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

function supportsTouchKeyboard() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768
        || ('ontouchstart' in window)
        || (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches);
}

function parseManifest(data: unknown): CrosswordManifest {
    const candidate = data as Partial<CrosswordManifest>;
    if (
        !candidate
        || !candidate.grid
        || !Array.isArray(candidate.grid.pattern)
        || !Array.isArray(candidate.grid.solution)
        || !Array.isArray(candidate.grid.entries)
        || !candidate.grid.pattern.length
    ) {
        throw new Error('The crossword manifest is incomplete.');
    }
    return candidate as CrosswordManifest;
}

export default function MovieCrossword({country}: { country: 'south-africa' | 'nigeria' }) {
    const [manifest, setManifest] = useState<CrosswordManifest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [letters, setLetters] = useState<Record<string, string>>({});
    const [selected, setSelected] = useState<Cell | null>(null);
    const [direction, setDirection] = useState<Direction>('across');
    const [wrongCells, setWrongCells] = useState<Set<string>>(new Set());
    const [hintCount, setHintCount] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [checkMessage, setCheckMessage] = useState('');
    const cellRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const mobileInputRef = useRef<HTMLInputElement | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        fetchMovieCrosswordManifest()
            .then(response => {
                const nextManifest = parseManifest(response.data);
                const firstEntry = nextManifest.grid.entries[0];
                setManifest(nextManifest);
                setLetters({});
                setWrongCells(new Set());
                setHintCount(0);
                setCompleted(false);
                setCheckMessage('');
                setDirection(firstEntry?.direction || 'across');
                setSelected(firstEntry
                    ? {row: firstEntry.row, column: firstEntry.column}
                    : {row: 0, column: 0});
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!selected) return;
        cellRefs.current[cellKey(selected)]?.focus();
        if (supportsTouchKeyboard()) mobileInputRef.current?.focus();
    }, [selected]);

    const openCells = useMemo<Cell[]>(() => {
        if (!manifest) return [];
        const cells: Cell[] = [];
        for (let row = 0; row < manifest.grid.rows; row += 1) {
            for (let column = 0; column < manifest.grid.columns; column += 1) {
                if (manifest.grid.pattern[row]?.[column] !== '#') cells.push({row, column});
            }
        }
        return cells;
    }, [manifest]);

    const numberByCell = useMemo<Record<string, number>>(() => {
        const numbers: Record<string, number> = {};
        manifest?.grid.entries.forEach(entry => {
            numbers[cellKey({row: entry.row, column: entry.column})] = entry.number;
        });
        return numbers;
    }, [manifest]);

    const activeEntry = useMemo<CrosswordEntry | null>(() => {
        if (!manifest || !selected) return null;
        return manifest.grid.entries.find(entry =>
            entry.direction === direction && containsCell(entry, selected)
        ) || manifest.grid.entries.find(entry => containsCell(entry, selected)) || null;
    }, [direction, manifest, selected]);

    const activeCellKeys = useMemo(() => {
        const keys = new Set<string>();
        if (!activeEntry) return keys;
        entryCells(activeEntry).forEach(cell => keys.add(cellKey(cell)));
        return keys;
    }, [activeEntry]);

    const activeEntryCells = useMemo(() => activeEntry ? entryCells(activeEntry) : [], [activeEntry]);
    const mobileEntryValue = useMemo(
        () => activeEntryCells.map(cell => letters[cellKey(cell)] || '').join(''),
        [activeEntryCells, letters]
    );

    const isSolved = useCallback((candidate: Record<string, string>) => {
        if (!manifest) return false;
        return openCells.every(cell => candidate[cellKey(cell)] === manifest.grid.solution[cell.row]?.[cell.column]);
    }, [manifest, openCells]);

    const selectCell = useCallback((cell: Cell, preferredDirection?: Direction) => {
        if (!manifest || manifest.grid.pattern[cell.row]?.[cell.column] === '#') return;
        const choices = manifest.grid.entries.filter(entry => containsCell(entry, cell));
        let nextDirection = preferredDirection || direction;
        const sameCell = selected?.row === cell.row && selected?.column === cell.column;
        const hasAcross = choices.some(entry => entry.direction === 'across');
        const hasDown = choices.some(entry => entry.direction === 'down');
        if (!preferredDirection && sameCell && hasAcross && hasDown) {
            nextDirection = direction === 'across' ? 'down' : 'across';
        } else if (!choices.some(entry => entry.direction === nextDirection)) {
            nextDirection = choices[0]?.direction || 'across';
        }
        setSelected(cell);
        setDirection(nextDirection);
        setCheckMessage('');
        if (supportsTouchKeyboard()) mobileInputRef.current?.focus();
    }, [direction, manifest, selected]);

    const selectEntry = useCallback((entry: CrosswordEntry) => {
        setSelected({row: entry.row, column: entry.column});
        setDirection(entry.direction);
        setCheckMessage('');
        if (supportsTouchKeyboard()) mobileInputRef.current?.focus();
    }, []);

    const writeLetter = useCallback((cell: Cell, value: string) => {
        if (!manifest || completed) return;
        const key = cellKey(cell);
        const nextLetters = {...letters, [key]: value};
        setLetters(nextLetters);
        setWrongCells(previous => {
            const next = new Set(previous);
            next.delete(key);
            return next;
        });
        setCheckMessage('');
        if (isSolved(nextLetters)) {
            mobile.haptic('success');
            setCompleted(true);
            setCheckMessage('Solved! The movie notes are unlocked below.');
        }
    }, [completed, isSolved, letters, manifest]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!manifest || !selected || completed) return;
        if (event.target instanceof HTMLInputElement) return;
        const key = event.key;
        if (key === ' ' || key === 'Tab') {
            if (key === ' ') {
                event.preventDefault();
                setDirection(previous => previous === 'across' ? 'down' : 'across');
            }
            return;
        }
        if (key === 'ArrowLeft' || key === 'ArrowRight') {
            event.preventDefault();
            const step = key === 'ArrowLeft' ? -1 : 1;
            const next = nextOpenCell(manifest, selected, 'across', step);
            if (next) selectCell(next, 'across');
            return;
        }
        if (key === 'ArrowUp' || key === 'ArrowDown') {
            event.preventDefault();
            const step = key === 'ArrowUp' ? -1 : 1;
            const next = nextOpenCell(manifest, selected, 'down', step);
            if (next) selectCell(next, 'down');
            return;
        }
        if (key === 'Backspace' || key === 'Delete') {
            event.preventDefault();
            const currentKey = cellKey(selected);
            if (letters[currentKey]) {
                setLetters(previous => {
                    const next = {...previous};
                    delete next[currentKey];
                    return next;
                });
                setWrongCells(previous => {
                    const next = new Set(previous);
                    next.delete(currentKey);
                    return next;
                });
                return;
            }
            const previousCell = nextOpenCell(manifest, selected, direction, -1);
            if (previousCell) {
                const previousKey = cellKey(previousCell);
                setSelected(previousCell);
                setLetters(previous => {
                    const next = {...previous};
                    delete next[previousKey];
                    return next;
                });
            }
            return;
        }
        if (/^[a-z]$/i.test(key)) {
            event.preventDefault();
            writeLetter(selected, key.toUpperCase());
            const next = nextOpenCell(manifest, selected, direction, 1);
            if (next) setSelected(next);
        }
    }, [completed, direction, letters, manifest, selectCell, selected, writeLetter]);

    const handleMobileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (!manifest || !activeEntry || completed) return;
        const value = event.target.value.replace(/[^a-z]/gi, '').toUpperCase().slice(0, activeEntry.length);
        const nextLetters = {...letters};
        activeEntryCells.forEach((cell, index) => {
            const key = cellKey(cell);
            if (value[index]) {
                nextLetters[key] = value[index];
            } else {
                delete nextLetters[key];
            }
        });
        setLetters(nextLetters);
        setWrongCells(previous => {
            const next = new Set(previous);
            activeEntryCells.forEach(cell => next.delete(cellKey(cell)));
            return next;
        });
        setCheckMessage('');
        if (isSolved(nextLetters)) {
            mobile.haptic('success');
            setCompleted(true);
            setCheckMessage('Solved! The movie notes are unlocked below.');
        }
    }, [activeEntry, activeEntryCells, completed, isSolved, letters, manifest]);

    const checkPuzzle = useCallback(() => {
        if (!manifest) return;
        const wrong = new Set<string>();
        let emptyCount = 0;
        openCells.forEach(cell => {
            const key = cellKey(cell);
            const value = letters[key];
            if (!value) {
                emptyCount += 1;
            } else if (value !== manifest.grid.solution[cell.row]?.[cell.column]) {
                wrong.add(key);
            }
        });
        setWrongCells(wrong);
        if (wrong.size) {
            mobile.haptic('warning');
            setCheckMessage(`${wrong.size} square${wrong.size === 1 ? '' : 's'} ${wrong.size === 1 ? "doesn't" : "don't"} match yet.`);
        } else if (emptyCount) {
            setCheckMessage(`${emptyCount} square${emptyCount === 1 ? '' : 's'} still ${emptyCount === 1 ? 'needs' : 'need'} an answer.`);
        } else {
            mobile.haptic('success');
            setCompleted(true);
            setCheckMessage('Solved! The movie notes are unlocked below.');
        }
    }, [letters, manifest, openCells]);

    const giveHint = useCallback(() => {
        if (!manifest || completed) return;
        const target = (selected && !letters[cellKey(selected)] ? selected : null)
            || openCells.find(cell => !letters[cellKey(cell)]);
        if (!target) return;
        mobile.haptic('light');
        writeLetter(target, manifest.grid.solution[target.row]?.[target.column] || '');
        setSelected(target);
        setHintCount(previous => previous + 1);
        setCheckMessage('One square revealed.');
    }, [completed, letters, manifest, openCells, selected, writeLetter]);

    const resetPuzzle = useCallback(() => {
        setLetters({});
        setWrongCells(new Set());
        setHintCount(0);
        setCompleted(false);
        setCheckMessage('');
        if (manifest?.grid.entries[0]) {
            const firstEntry = manifest.grid.entries[0];
            setSelected({row: firstEntry.row, column: firstEntry.column});
            setDirection(firstEntry.direction);
        }
    }, [manifest]);

    const filledCount = openCells.filter(cell => letters[cellKey(cell)]).length;
    const dateLabel = formatPuzzleDate(manifest?.date);

    return (
        <div className="container cw-page" onKeyDown={handleKeyDown}>
            <a href={`/game-studio/${country}`} className="game-back-link">&larr; Back to Games</a>

            <header className="cw-header">
                <div className="cw-badge">🎬 Daily puzzle</div>
                <h1>Movie Crossword</h1>
                <p>{manifest?.theme || 'A fresh crossword built from classic movie trivia.'}</p>
                <div className="cw-meta">
                    {dateLabel && <span className="cw-meta-chip">📅 {dateLabel}</span>}
                    <span className="cw-meta-chip">🎞️ Before 2010</span>
                    <span className="cw-meta-chip">🧠 Medium</span>
                </div>
            </header>

            {loading && <div className="cw-loading" role="status">Loading today's crossword<span className="loading-dots" /></div>}

            {error && !loading && (
                <div className="cw-error">
                    <div className="cw-error-emoji">🎞️</div>
                    <h2>Today's puzzle is still in the editing room</h2>
                    <p>We couldn't reach the crossword archive. Check back soon or try again.</p>
                    <button className="center-btn" onClick={load}>Try again</button>
                </div>
            )}

            {manifest && !loading && (
                <main className="cw-main">
                    <section className="cw-board-panel">
                        <div className="cw-board-heading">
                            <div>
                                <span className="cw-section-label">Puzzle grid</span>
                                <h2>{manifest.title}</h2>
                            </div>
                            <div className="cw-progress"><strong>{filledCount}</strong>/{openCells.length}</div>
                        </div>

                        <div
                            className="cw-grid"
                            role="grid"
                            aria-label="Movie crossword grid"
                            style={{
                                gridTemplateColumns: `repeat(${manifest.grid.columns}, minmax(0, 1fr))`,
                                aspectRatio: `${manifest.grid.columns} / ${manifest.grid.rows}`,
                            }}
                        >
                            {manifest.grid.pattern.map((row, rowIndex) => Array.from(row).map((cellValue, columnIndex) => {
                                const position = {row: rowIndex, column: columnIndex};
                                const key = cellKey(position);
                                if (cellValue === '#') {
                                    return <div key={key} className="cw-cell cw-block" aria-hidden="true" />;
                                }
                                const isActive = activeCellKeys.has(key);
                                const isSelected = selected ? cellKey(selected) === key : false;
                                const value = letters[key] || '';
                                const className = [
                                    'cw-cell',
                                    isActive ? 'cw-cell-active-entry' : '',
                                    isSelected ? 'cw-cell-selected' : '',
                                    wrongCells.has(key) ? 'cw-cell-wrong' : '',
                                ].filter(Boolean).join(' ');
                                const number = numberByCell[key];
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={className}
                                        aria-label={`${number ? `Cell ${number}` : 'Cell'} row ${rowIndex + 1} column ${columnIndex + 1}${value ? `, ${value}` : ''}`}
                                        onClick={() => selectCell(position)}
                                        ref={element => { cellRefs.current[key] = element; }}
                                    >
                                        {number && <span className="cw-cell-number">{number}</span>}
                                        <span className="cw-cell-letter">{value}</span>
                                    </button>
                                );
                            }))}
                        </div>

                        <div className="cw-mobile-entry">
                            <label htmlFor="cw-word-input">Type the selected answer</label>
                            <div className="cw-mobile-entry-control">
                                <input
                                    ref={mobileInputRef}
                                    id="cw-word-input"
                                    value={mobileEntryValue}
                                    onChange={handleMobileInputChange}
                                    onFocus={event => event.currentTarget.select()}
                                    maxLength={activeEntry?.length || 1}
                                    inputMode="text"
                                    autoCapitalize="characters"
                                    autoComplete="off"
                                    spellCheck={false}
                                    aria-label="Enter the selected word"
                                />
                                <span>
                                    {activeEntry
                                        ? `${activeEntry.number} ${activeEntry.direction} · ${activeEntry.length} letters`
                                        : 'Select a clue'}
                                </span>
                            </div>
                        </div>

                        <p className="cw-help">Click a square twice to switch direction. Type letters, use arrows to move, and press Space to switch across/down.</p>
                        <div className="cw-actions">
                            <button type="button" className="cw-action cw-action-primary" onClick={checkPuzzle}>Check</button>
                            <button type="button" className="cw-action" onClick={giveHint} disabled={completed}>
                                Hint{hintCount > 0 ? ` · ${hintCount}` : ''}
                            </button>
                            <button type="button" className="cw-action" onClick={resetPuzzle}>Clear</button>
                        </div>
                        <div className="cw-status" role="status" aria-live="polite">
                            {checkMessage || (activeEntry ? `${activeEntry.number} ${activeEntry.direction}: ${activeEntry.clue}` : 'Select a clue to begin.')}
                        </div>
                    </section>

                    <aside className="cw-clues" aria-label="Crossword clues">
                        {(['across', 'down'] as Direction[]).map(clueDirection => (
                            <section key={clueDirection} className="cw-clue-section">
                                <h2>{clueDirection === 'across' ? 'Across' : 'Down'}</h2>
                                <div className="cw-clue-list">
                                    {manifest.grid.entries
                                        .filter(entry => entry.direction === clueDirection)
                                        .map(entry => (
                                            <button
                                                key={entryKey(entry)}
                                                type="button"
                                                className={`cw-clue ${activeEntry && entryKey(activeEntry) === entryKey(entry) ? 'cw-clue-active' : ''}`}
                                                onClick={() => selectEntry(entry)}
                                                aria-pressed={Boolean(activeEntry && entryKey(activeEntry) === entryKey(entry))}
                                            >
                                                <span className="cw-clue-number">{entry.number}</span>
                                                <span>{entry.clue}</span>
                                                <small>{entry.length}</small>
                                            </button>
                                        ))}
                                </div>
                            </section>
                        ))}
                    </aside>
                </main>
            )}

            {completed && manifest && (
                <section className="cw-facts">
                    <div className="cw-facts-heading">
                        <div className="cw-badge cw-badge-solved">✨ Solved</div>
                        <h2>Behind the scenes</h2>
                        <p>Here are the films behind today's clues.</p>
                        <button type="button" className="cw-action" onClick={() => mobile.share({
                            title: manifest.title,
                            text: "I solved today's Movie Crossword.",
                            url: window.location.href,
                        })}>
                            📤 Share
                        </button>
                    </div>
                    <div className="cw-facts-grid">
                        {manifest.grid.entries.map(entry => (
                            <article key={entryKey(entry)} className="cw-fact">
                                <div className="cw-fact-top">
                                    <strong>{entry.answer || entry.displayAnswer}</strong>
                                    {entry.year && <span>{entry.year}</span>}
                                </div>
                                <h3>{entry.movie}</h3>
                                <p>{entry.trivia}</p>
                            </article>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
